// ── Screen layout: phone, tablet or desktop ──
// Desktop (three columns: sidebar · map · panel) is the app as it has always
// been. Narrower screens keep the same components and rearrange them: a
// tablet slides the sidebar in from a ☰ button; a phone does too, fills the
// screen with the map and turns the right-hand panel into a bottom sheet.
// App puts the layout on its root as a class (layout-phone / -tablet /
// -desktop); every narrow-screen style hangs off those classes, so the
// desktop styles are untouched.

import { useEffect, useState } from "react";

export type Layout = "phone" | "tablet" | "desktop";
export type SheetSnap = "peek" | "half" | "full";

// a phone held sideways is short rather than narrow
const PHONE = "(max-width: 767px), (max-height: 500px)";
const TABLET = "(max-width: 1099px)";

const media = (q: string) =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(q) : null;

export function detectLayout(): Layout {
  if (media(PHONE)?.matches) return "phone";
  if (media(TABLET)?.matches) return "tablet";
  return "desktop";
}

/** Calls `cb` with the layout whenever the screen crosses a breakpoint. */
export function watchLayout(cb: (l: Layout) => void): () => void {
  const qs = [media(PHONE), media(TABLET)].filter((q): q is MediaQueryList => !!q);
  const on = () => cb(detectLayout());
  qs.forEach((q) => q.addEventListener("change", on));
  return () => qs.forEach((q) => q.removeEventListener("change", on));
}

export const SHEET_SNAPS: SheetSnap[] = ["peek", "half", "full"];
export const SHEET_PEEK = 112; // the grip and a two-line summary

/** The phone's bottom sheet height (px) at a snap, for a screen `vh` tall
 *  (the home-indicator inset is added on top in CSS). */
export function sheetHeight(snap: SheetSnap, vh: number): number {
  if (snap === "full") return Math.max(SHEET_PEEK, vh - 64); // the ☰ row stays in view
  if (snap === "half") return Math.max(SHEET_PEEK, Math.round(vh * 0.5));
  return SHEET_PEEK;
}

/** The visible height of the screen (follows the mobile address bar). */
export function useViewportHeight(): number {
  const read = () => (typeof window === "undefined" ? 800 : Math.round(window.visualViewport?.height ?? window.innerHeight));
  const [vh, setVh] = useState(read);
  useEffect(() => {
    const on = () => setVh(read());
    window.addEventListener("resize", on);
    window.visualViewport?.addEventListener("resize", on);
    return () => {
      window.removeEventListener("resize", on);
      window.visualViewport?.removeEventListener("resize", on);
    };
  }, []);
  return vh;
}
